import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Modal } from "react-bootstrap";
import { useSelector, useDispatch } from "react-redux";
import Swal from "sweetalert2";
import ReactApexChart from "react-apexcharts";
import {
  appApi,
  useGetAllStaffQuery,
  useGetContractorsQuery,
  useGetSuppliersQuery,
  useGetCategoryExpenseSummariesQuery,
  useGetWorkProgressListQuery,
  useGetExpensesQuery,
  useGetMyCalendarQuery,
  useGetHolidaysQuery,
} from "../../../store/appApi";
import {
  createHolidayApi,
  deleteHolidayApi,
} from "../../../services/holidays";
import {
  selectHasModuleAccess,
  selectUserModules,
  selectUserPagePermissions,
  selectUserRole,
} from "../../../store/authSlice";

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatINR(amount) {
  if (!amount || isNaN(amount)) return "₹0";
  if (amount >= 1_00_000) return `₹${(amount / 1_00_000).toFixed(1)}L`;
  return `₹${Math.round(amount).toLocaleString("en-IN")}`;
}

function todayMonthStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function todayDateStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;
}

function formatDate(dateStr) {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
  });
}

function txnLabel(entry) {
  if (entry.isVehicleExpense && entry.vehicle?.name)
    return `Fuel — ${entry.vehicle.name}`;
  if (entry.isLabourPayment) return "Labour payment";
  if (entry.isContractorPayment && entry.contractor?.name)
    return `Contractor — ${entry.contractor.name}`;
  if (entry.isSupplierPayment && entry.supplier?.name)
    return `Supplier — ${entry.supplier.name}`;
  return entry.category?.name || entry.desc || "Expense";
}

// ── Sub-components ────────────────────────────────────────────────────────────

const StatCard = ({ title, value, icon, color, to, loading }) => (
  <Link to={to} style={{ textDecoration: "none" }}>
    <div className="widget-stat card">
      <div className="card-body p-4">
        <div className="media ai-icon">
          <span className={`me-3 bgl-${color} text-${color}`}>
            <i className={icon} style={{ fontSize: 24 }} />
          </span>
          <div className="media-body">
            <p className="mb-1">{title}</p>
            <h4 className="mb-0">
              {loading ? (
                <span
                  className={`spinner-border spinner-border-sm text-${color}`}
                  style={{ width: 18, height: 18 }}
                />
              ) : (
                value
              )}
            </h4>
          </div>
        </div>
      </div>
    </div>
  </Link>
);

const SectionLabel = ({ children }) => (
  <p
    className="mb-3"
    style={{
      fontSize: 11,
      fontWeight: 600,
      color: "#888",
      textTransform: "uppercase",
      letterSpacing: "0.07em",
    }}
  >
    {children}
  </p>
);

const CardSpinner = ({ height = 280 }) => (
  <div
    className="d-flex justify-content-center align-items-center"
    style={{ height }}
  >
    <div className="spinner-border text-primary spinner-border-sm" />
  </div>
);

const CardEmpty = ({ icon, message, height = 280 }) => (
  <div
    className="d-flex flex-column justify-content-center align-items-center text-muted"
    style={{ height, fontSize: 13 }}
  >
    <i className={`${icon} mb-2`} style={{ fontSize: 32 }} />
    {message}
  </div>
);

// Quick attendance access — spares the employee from opening the sidebar every
// time they need to punch. The actual punch flow (GPS + selfie) still lives on
// the dedicated page; this just surfaces today's status and a one-tap shortcut.
const AttendanceQuickCard = () => {
  const { data, isLoading } = useGetMyCalendarQuery(todayMonthStr());
  const todayRecord = data?.days?.find((d) => d.date === todayDateStr());
  const punchedIn = !!todayRecord?.punchIn;
  const punchedOut = !!todayRecord?.punchOut;

  let statusText = "Not punched in yet today";
  let statusColor = "#888";
  if (punchedOut) {
    statusText = `Punched out at ${new Date(
      todayRecord.punchOut,
    ).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
    statusColor = "#22c55e";
  } else if (punchedIn) {
    statusText = `Punched in at ${new Date(
      todayRecord.punchIn,
    ).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
    statusColor = "#f59e0b";
  }

  const showPunchOut = punchedIn && !punchedOut;
  const btnLabel = punchedOut
    ? "View attendance"
    : showPunchOut
      ? "Punch Out"
      : "Punch In";
  const btnClass = punchedOut
    ? "btn-outline-secondary"
    : showPunchOut
      ? "btn-danger"
      : "btn-success";

  return (
    <div className="card mb-4">
      <div className="card-body p-4">
        <div className="d-flex align-items-center flex-wrap gap-3">
          <div
            style={{
              width: 46,
              height: 46,
              borderRadius: 12,
              background: "#e0f2fe",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <i
              className="fa-solid fa-clipboard-user"
              style={{ fontSize: 20, color: "#0284c7" }}
            />
          </div>
          <div style={{ flex: 1, minWidth: 160 }}>
            <p className="mb-1" style={{ fontSize: 13, fontWeight: 600 }}>
              Attendance
            </p>
            <span style={{ fontSize: 12, color: statusColor, fontWeight: 500 }}>
              {isLoading ? "Loading…" : statusText}
            </span>
          </div>
          <Link
            to="staff/attendance/punch"
            className={`btn ${btnClass}`}
            style={{ minWidth: 130 }}
          >
            {btnLabel}
          </Link>
        </div>
      </div>
    </div>
  );
};

// Contract Workers → quick access to the group-attendance screen, so a
// super-admin doesn't need to open the sidebar. Mirrors AttendanceQuickCard
// but for the separate Contract Workers module.
const ContractWorkerAttendanceQuickCard = () => {
  const role = useSelector(selectUserRole);
  if (role !== "super-admin") return null;

  return (
    <div className="card mb-4">
      <div className="card-body p-4">
        <div className="d-flex align-items-center flex-wrap gap-3">
          <div
            style={{
              width: 46,
              height: 46,
              borderRadius: 12,
              background: "#fef3c7",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <i
              className="fa-solid fa-helmet-safety"
              style={{ fontSize: 20, color: "#d97706" }}
            />
          </div>
          <div style={{ flex: 1, minWidth: 160 }}>
            <p className="mb-1" style={{ fontSize: 13, fontWeight: 600 }}>
              Contract Workers
            </p>
            <span style={{ fontSize: 12, color: "#888", fontWeight: 500 }}>
              Mark today's group attendance
            </span>
          </div>
          <Link
            to="contract-workers/attendance"
            className="btn btn-warning"
            style={{ minWidth: 130 }}
          >
            Mark Attendance
          </Link>
        </div>
      </div>
    </div>
  );
};

// Super-admin: declare a company holiday over a From–To range. It shows on
// every employee's attendance calendar (or, employee-wise, only on the picked
// employees') and is excluded from Performance Review attendance penalties.
const fmtShort = (v) =>
  new Date(v).toLocaleDateString("en-IN", { day: "2-digit", month: "short" });

const holidayLabel = (h) => {
  const from = String(h.date).slice(0, 10);
  const to = String(h.endDate || h.date).slice(0, 10);
  return from === to ? fmtShort(h.date) : `${fmtShort(h.date)} – ${fmtShort(h.endDate)}`;
};

// Searchable checkbox list of employees for an employee-wise holiday.
const HolidayEmployeeModal = ({ show, staff, selected, onClose, onApply }) => {
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState(new Set(selected));

  const onEnter = () => {
    setQ("");
    setPicked(new Set(selected));
  };

  const term = q.trim().toLowerCase();
  const visible = staff.filter(
    (s) =>
      !term ||
      (s.name || "").toLowerCase().includes(term) ||
      (s.phone || "").includes(term) ||
      (s.role || "").toLowerCase().includes(term),
  );
  const allVisiblePicked =
    visible.length > 0 && visible.every((s) => picked.has(String(s._id)));

  const toggle = (id) => {
    const next = new Set(picked);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setPicked(next);
  };
  const toggleAll = () => {
    const next = new Set(picked);
    visible.forEach((s) =>
      allVisiblePicked ? next.delete(String(s._id)) : next.add(String(s._id)),
    );
    setPicked(next);
  };

  return (
    <Modal show={show} onHide={onClose} onEnter={onEnter} centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 16 }}>Select employees</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <input
          type="text"
          className="form-control mb-3"
          placeholder="Search by name, phone or role"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <label
          className="d-flex align-items-center gap-2 mb-2"
          style={{ fontSize: 13, fontWeight: 600, cursor: "pointer" }}
        >
          <input
            type="checkbox"
            checked={allVisiblePicked}
            onChange={toggleAll}
            disabled={!visible.length}
          />
          Select all{term ? " (matching)" : ""}
          <span className="ms-auto" style={{ color: "#888", fontWeight: 500 }}>
            {picked.size} selected
          </span>
        </label>
        <div style={{ maxHeight: 360, overflowY: "auto" }}>
          {visible.map((s) => (
            <label
              key={s._id}
              className="d-flex align-items-center gap-2 py-2 border-bottom"
              style={{ fontSize: 13, cursor: "pointer" }}
            >
              <input
                type="checkbox"
                checked={picked.has(String(s._id))}
                onChange={() => toggle(String(s._id))}
              />
              <span style={{ fontWeight: 600 }}>{s.name}</span>
              <span className="ms-auto" style={{ color: "#888", fontSize: 12 }}>
                {s.role}
              </span>
            </label>
          ))}
          {!visible.length && (
            <p className="text-center mb-0 py-3" style={{ color: "#888", fontSize: 13 }}>
              No employees found
            </p>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button className="btn btn-light" onClick={() => setPicked(new Set())}>
          Clear
        </button>
        <button
          className="btn"
          style={{ background: "#7c3aed", color: "#fff" }}
          onClick={() => onApply([...picked])}
        >
          Apply
        </button>
      </Modal.Footer>
    </Modal>
  );
};

const HolidayDeclareCard = () => {
  const role = useSelector(selectUserRole);
  const dispatch = useDispatch();
  const today = new Date().toISOString().slice(0, 10);
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const [name, setName] = useState("");
  const [employeeIds, setEmployeeIds] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const isSuperAdmin = role === "super-admin";
  const { data } = useGetHolidaysQuery(
    { from: today },
    { skip: !isSuperAdmin },
  );
  const { data: staffData } = useGetAllStaffQuery(
    { limit: 1000, allProjects: true },
    { skip: !isSuperAdmin },
  );
  const holidays = data?.holidays || [];
  const staff = useMemo(
    () =>
      [...(staffData?.staff || [])].sort((a, b) =>
        (a.name || "").localeCompare(b.name || ""),
      ),
    [staffData],
  );

  if (!isSuperAdmin) return null;

  const refresh = () => dispatch(appApi.util.invalidateTags(["Holidays", "AttendanceCalendar"]));

  // To follows From when From moves past it.
  const onFrom = (v) => {
    setFrom(v);
    if (!to || to < v) setTo(v);
  };

  const add = async () => {
    if (!name.trim()) {
      Swal.fire({ icon: "warning", title: "Enter the holiday name." });
      return;
    }
    if (!from || !to) {
      Swal.fire({ icon: "warning", title: "Pick the From and To dates." });
      return;
    }
    if (to < from) {
      Swal.fire({ icon: "warning", title: "To date can't be before From date." });
      return;
    }
    try {
      setBusy(true);
      await createHolidayApi({ date: from, endDate: to, name: name.trim(), employeeIds });
      setName("");
      setEmployeeIds([]);
      refresh();
      Swal.fire({ icon: "success", title: "Holiday declared", timer: 1100, showConfirmButton: false });
    } catch (e) {
      Swal.fire({ icon: "error", title: e?.response?.data?.message || "Failed" });
    } finally {
      setBusy(false);
    }
  };
  const remove = async (id) => {
    try {
      await deleteHolidayApi(id);
      refresh();
    } catch (e) {
      Swal.fire({ icon: "error", title: e?.response?.data?.message || "Failed" });
    }
  };

  return (
    <div className="card mb-4">
      <div className="card-body p-4">
        <div className="d-flex align-items-center flex-wrap gap-3">
          <div
            style={{
              width: 46,
              height: 46,
              borderRadius: 12,
              background: "#ede9fe",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <i
              className="fa-solid fa-calendar-day"
              style={{ fontSize: 20, color: "#7c3aed" }}
            />
          </div>
          <div style={{ flex: 1, minWidth: 160 }}>
            <p className="mb-1" style={{ fontSize: 13, fontWeight: 600 }}>
              Declare Holiday
            </p>
            <span style={{ fontSize: 12, color: "#888", fontWeight: 500 }}>
              {employeeIds.length
                ? `Shows on ${employeeIds.length} selected employee${employeeIds.length === 1 ? "" : "s"}' calendars`
                : "Shows on every employee's calendar"}
            </span>
          </div>
          <div className="d-flex align-items-center gap-2">
            <span style={{ fontSize: 12, color: "#888" }}>From</span>
            <input
              type="date"
              className="form-control"
              style={{ maxWidth: 160 }}
              value={from}
              onChange={(e) => onFrom(e.target.value)}
            />
            <span style={{ fontSize: 12, color: "#888" }}>To</span>
            <input
              type="date"
              className="form-control"
              style={{ maxWidth: 160 }}
              value={to}
              min={from}
              onChange={(e) => setTo(e.target.value)}
            />
          </div>
          <input
            type="text"
            className="form-control"
            style={{ maxWidth: 220 }}
            placeholder="Holiday name (e.g. Diwali)"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <button
            className="btn btn-outline-secondary"
            style={{ minWidth: 150 }}
            onClick={() => setPickerOpen(true)}
            title="Apply only to selected employees"
          >
            <i className="fa-solid fa-user-check me-2" />
            {employeeIds.length ? `${employeeIds.length} employee${employeeIds.length === 1 ? "" : "s"}` : "Employee-wise"}
          </button>
          <button
            className="btn"
            style={{ minWidth: 120, background: "#7c3aed", color: "#fff" }}
            disabled={busy}
            onClick={add}
          >
            {busy ? "…" : "Declare"}
          </button>
        </div>

        {holidays.length > 0 && (
          <div className="mt-3 d-flex flex-wrap gap-2">
            {holidays.map((h) => {
              const emps = h.employeeIds || [];
              return (
                // Own pill instead of .badge — the theme's badge sizing cut
                // the background off before the employee count and ×.
                <span
                  key={h._id}
                  className="d-inline-flex align-items-center gap-2"
                  style={{
                    background: "#f3f0ff",
                    color: "#5b21b6",
                    fontWeight: 600,
                    fontSize: 12,
                    lineHeight: 1.4,
                    padding: "6px 10px",
                    borderRadius: 8,
                    whiteSpace: "nowrap",
                    width: "auto",
                    height: "auto",
                  }}
                  title={emps.length ? emps.map((e) => e?.name || "").join(", ") : "Everyone"}
                >
                  {holidayLabel(h)} · {h.name}
                  {emps.length > 0 && (
                    <span style={{ color: "#7c3aed", fontWeight: 500 }}>
                      · {emps.length} employee{emps.length === 1 ? "" : "s"}
                    </span>
                  )}
                  <button
                    onClick={() => remove(h._id)}
                    style={{
                      border: "none",
                      background: "transparent",
                      color: "#a78bfa",
                      fontWeight: 700,
                      cursor: "pointer",
                    }}
                    title="Remove"
                  >
                    ×
                  </button>
                </span>
              );
            })}
          </div>
        )}
      </div>

      <HolidayEmployeeModal
        show={pickerOpen}
        staff={staff}
        selected={employeeIds}
        onClose={() => setPickerOpen(false)}
        onApply={(ids) => {
          setEmployeeIds(ids);
          setPickerOpen(false);
        }}
      />
    </div>
  );
};

// ── Chart configs ─────────────────────────────────────────────────────────────

const CHART_COLORS = [
  "#378ADD",
  "#1D9E75",
  "#EF9F27",
  "#E24B4A",
  "#884AB7",
  "#f97316",
  "#06b6d4",
];
const HEAT_COLORS = ["#f0f4f8", "#B5D4F4", "#378ADD", "#185FA5"];

function buildDonutConfig(rows, grandTotal) {
  return {
    series: rows.map((r) => r.totalExpense),
    options: {
      chart: { type: "donut", toolbar: { show: false } },
      labels: rows.map((r) => r.categoryName),
      colors: CHART_COLORS,
      legend: {
        position: "bottom",
        fontSize: "16px",
        itemMargin: { horizontal: 6, vertical: 4 },
      },
      plotOptions: {
        pie: {
          donut: {
            size: "65%",
            labels: {
              show: true,
              total: {
                show: true,
                label: "Total",
                fontSize: "16px",
                fontWeight: 600,
                formatter: () => formatINR(grandTotal),
              },
            },
          },
        },
      },
      dataLabels: { enabled: false },
      tooltip: { y: { formatter: (val) => formatINR(val) } },
    },
  };
}

// Red = expense (payments), green = receipts — never mixed into one bar.
const EXPENSE_COLOR = "#E24B4A";
const RECEIPT_COLOR = "#1D9E75";

function buildBarConfig(rows) {
  return {
    series: [
      { name: "Expense", data: rows.map((r) => r.totalExpense || 0) },
      { name: "Receipt", data: rows.map((r) => r.totalReceipt || 0) },
    ],
    options: {
      chart: { type: "bar", toolbar: { show: false } },
      plotOptions: {
        bar: { horizontal: true, borderRadius: 4, barHeight: "70%" },
      },
      colors: [EXPENSE_COLOR, RECEIPT_COLOR],
      dataLabels: { enabled: false },
      xaxis: {
        categories: rows.map((r) => r.categoryName),
        labels: { formatter: (v) => formatINR(v), style: { fontSize: "14px" } },
      },
      yaxis: { labels: { style: { fontSize: "14px" } } },
      legend: { position: "top", fontSize: "14px" },
      tooltip: { shared: true, intersect: false, y: { formatter: (val) => formatINR(val) } },
      grid: { borderColor: "#f0f0f0" },
    },
  };
}

function buildHeatmap(reports) {
  const days = [];
  for (let i = 13; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    days.push({
      key: d.toDateString(),
      label: d.toLocaleDateString("en-IN", { weekday: "short" }),
      date: d,
    });
  }
  const countMap = {};
  (reports || []).forEach((r) => {
    const k = new Date(r.reportDateTime).toDateString();
    countMap[k] = (countMap[k] || 0) + 1;
  });
  return days.map((d) => ({ ...d, count: countMap[d.key] || 0 }));
}

// ── Main component ────────────────────────────────────────────────────────────

const Home = () => {
  // ── Module permissions ──────────────────────────────────────────────────────
  const hasFinance = useSelector(selectHasModuleAccess("finance"));
  const hasWorkforce = useSelector(selectHasModuleAccess("workforce"));
  const hasContractors = useSelector(selectHasModuleAccess("contractors"));
  const hasSuppliers = useSelector(selectHasModuleAccess("suppliers"));
  const hasWorkProgress = useSelector(selectHasModuleAccess("workProgress"));
  const hasInventory = useSelector(selectHasModuleAccess("inventory"));
  // Write-level checks — for quick-action links that navigate to write forms
  const hasFinanceWrite = useSelector(
    selectHasModuleAccess("finance", "read-write"),
  );
  const hasWpWrite = useSelector(
    selectHasModuleAccess("workProgress", "read-write"),
  );
  const hasInventoryWrite = useSelector(
    selectHasModuleAccess("inventory", "read-write"),
  );

  // ── Attendance punch shortcut visibility ───────────────────────────────────
  // Matches the sidebar's gate for "Punch In / Out": super-admin, workforce
  // module read access, or an explicit page permission on the punch page.
  const role = useSelector(selectUserRole);
  const userModules = useSelector(selectUserModules);
  const userPagePerms = useSelector(selectUserPagePermissions);
  const LEVELS = { none: 0, read: 1, "read-write": 2 };
  const canPunch =
    role === "super-admin" ||
    (LEVELS[userModules.workforce ?? "none"] ?? 0) >= LEVELS.read ||
    (LEVELS[userPagePerms["workforce.attendance.punch"] ?? "none"] ?? 0) >=
      LEVELS.read;

  // ── Data hooks — skip queries the user has no access to ─────────────────────
  const { data: staffData, isLoading: staffLoading } = useGetAllStaffQuery(
    { limit: 1 },
    { skip: !hasWorkforce },
  );
  const { data: contractorData, isLoading: contractorLoading } =
    useGetContractorsQuery(undefined, { skip: !hasContractors });
  const { data: supplierData, isLoading: supplierLoading } =
    useGetSuppliersQuery(undefined, { skip: !hasSuppliers });
  const { data: catData, isLoading: catLoading } =
    useGetCategoryExpenseSummariesQuery(undefined, { skip: !hasFinance });
  const { data: wpData, isLoading: wpLoading } = useGetWorkProgressListQuery(
    undefined,
    { skip: !hasWorkProgress },
  );
  const { data: expData, isLoading: expLoading } = useGetExpensesQuery(
    undefined,
    { skip: !hasFinance },
  );

  // ── Derived values ──────────────────────────────────────────────────────────
  const workforceCount = staffData?.total ?? 0;
  const contractorCount =
    contractorData?.total ?? contractorData?.docs?.length ?? 0;
  const supplierCount = supplierData?.suppliers?.length ?? 0;

  const catRows = catData?.rows ?? [];
  const grandTotal = catData?.grandTotal ?? 0; // payments only
  const grandReceipt = catData?.grandReceipt ?? 0;
  // Donut: only categories that actually have expense (payments).
  const top5 = catRows
    .filter((r) => r.totalExpense > 0)
    .sort((a, b) => b.totalExpense - a.totalExpense)
    .slice(0, 5);
  // Bar: top categories by money moved, expense and receipt side by side.
  const topMoved = catRows
    .filter((r) => r.totalExpense > 0 || r.totalReceipt > 0)
    .sort(
      (a, b) =>
        b.totalExpense + (b.totalReceipt || 0) - (a.totalExpense + (a.totalReceipt || 0)),
    )
    .slice(0, 6);

  const reports = wpData?.reports ?? [];
  const recentReports = reports.slice(0, 5);
  const heatmapDays = buildHeatmap(reports);

  const finances = expData?.finance ?? [];
  const recentTxns = finances.slice(0, 5);

  const donut = buildDonutConfig(top5, grandTotal);
  const bar = buildBarConfig(topMoved);

  // ── KPI cards — only include if user has module access ──────────────────────
  const kpiCards = [
    hasFinance && {
      title: "Total Expenditure",
      value: formatINR(grandTotal),
      loading: catLoading,
      icon: "la la-coins",
      color: "primary",
      to: "finance/category-summary",
    },
    hasWorkforce && {
      title: "Workforce",
      value: workforceCount,
      loading: staffLoading,
      icon: "la la-hard-hat",
      color: "info",
      to: "all-staff",
    },
    hasContractors && {
      title: "Contractors",
      value: contractorCount,
      loading: contractorLoading,
      icon: "la la-tools",
      color: "warning",
      to: "contractor-list",
    },
    hasSuppliers && {
      title: "Suppliers",
      value: supplierCount,
      loading: supplierLoading,
      icon: "la la-truck",
      color: "secondary",
      to: "supplier-list",
    },
    hasWorkProgress && {
      title: "Progress Reports",
      value: reports.length,
      loading: wpLoading,
      icon: "la la-clipboard-list",
      color: "success",
      to: "progress/workprogress/list",
    },
  ].filter(Boolean);

  // ── Quick-action links — write actions gated by read-write permission ────────
  const quickLinks = [
    hasWpWrite && {
      icon: "la la-clipboard-check",
      label: "Add progress report",
      to: "progress/workprogress/add",
      color: "#10b981",
      bg: "#dcfce7",
    },
    hasFinanceWrite && {
      icon: "la la-coins",
      label: "Record expense",
      to: "add/expense",
      color: "#324336",
      bg: "#ede9fe",
    },
    hasInventoryWrite && {
      icon: "la la-box-open",
      label: "Issue inventory",
      to: "inventory/issue",
      color: "#f97316",
      bg: "#ffedd5",
    },
    hasWorkforce && {
      icon: "la la-users",
      label: "View all staff",
      to: "all-staff",
      color: "#378ADD",
      bg: "#dbeafe",
    },
  ].filter(Boolean);

  const anyModuleEnabled = kpiCards.length > 0;

  // ── No modules assigned ─────────────────────────────────────────────────────
  if (!anyModuleEnabled) {
    return (
      <>
        {canPunch && <AttendanceQuickCard />}
        <ContractWorkerAttendanceQuickCard />
        <HolidayDeclareCard />
        <div
        className="d-flex flex-column align-items-center justify-content-center text-center"
        style={{ minHeight: 400, color: "#888" }}
      >
        <i
          className="la la-lock mb-3"
          style={{ fontSize: 48, color: "#c0bfff" }}
        />
        <h5 style={{ fontWeight: 600, color: "#444" }}>No modules assigned</h5>
        <p style={{ fontSize: 13, maxWidth: 340 }}>
          You don't have access to any modules for this project yet. Contact
          your administrator to get permissions assigned.
        </p>
        </div>
      </>
    );
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <>
      {/* ── Attendance quick access — no need to open the sidebar to punch ── */}
      {canPunch && <AttendanceQuickCard />}
      <ContractWorkerAttendanceQuickCard />
      <HolidayDeclareCard />

      {/* ── Row 1: KPI Cards ──────────────────────────────────────────────── */}
      <div className="row mb-4 g-3">
        {kpiCards.map((c) => (
          <div className="col-xl col-sm-6" key={c.title}>
            <StatCard {...c} />
          </div>
        ))}
      </div>

      {/* ── Row 2: Finance Charts (only if user has finance access) ───────── */}
      {hasFinance && (
        <>
          <SectionLabel>Finance overview</SectionLabel>
          <div className="row g-3 mb-4">
            <div className="col-md-5">
              <div className="card h-100">
                <div className="card-body">
                  <h6 className="card-title mb-1" style={{ fontSize: 13 }}>
                    Expense by category
                  </h6>
                  {catLoading ? (
                    <CardSpinner />
                  ) : top5.length === 0 ? (
                    <CardEmpty
                      icon="la la-chart-pie"
                      message="No expense data yet"
                    />
                  ) : (
                    <ReactApexChart
                      type="donut"
                      series={donut.series}
                      options={donut.options}
                      height={300}
                    />
                  )}
                </div>
              </div>
            </div>

            <div className="col-md-7">
              <div className="card h-100">
                <div className="card-body">
                  <h6 className="card-title mb-1" style={{ fontSize: 13 }}>
                    Expense vs receipt by category
                  </h6>
                  <div className="mb-1" style={{ fontSize: 13 }}>
                    <span style={{ color: EXPENSE_COLOR, fontWeight: 600 }}>
                      Expense {formatINR(grandTotal)}
                    </span>
                    <span className="mx-2 text-muted">·</span>
                    <span style={{ color: RECEIPT_COLOR, fontWeight: 600 }}>
                      Receipt {formatINR(grandReceipt)}
                    </span>
                  </div>
                  {catLoading ? (
                    <CardSpinner />
                  ) : topMoved.length === 0 ? (
                    <CardEmpty
                      icon="la la-bar-chart"
                      message="No finance data yet"
                    />
                  ) : (
                    <ReactApexChart
                      type="bar"
                      series={bar.series}
                      options={bar.options}
                      height={300}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── Row 3: Work Progress (only if user has workProgress access) ──── */}
      {hasWorkProgress && (
        <>
          <SectionLabel>Work progress</SectionLabel>
          <div className="row g-3 mb-4">
            {/* Heatmap */}
            <div className="col-md-6">
              <div className="card h-100">
                <div className="card-body">
                  <h6 className="card-title" style={{ fontSize: 13 }}>
                    Activity — last 14 days
                  </h6>
                  {wpLoading ? (
                    <CardSpinner height={120} />
                  ) : (
                    <>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(14, 1fr)",
                          gap: 5,
                          marginBottom: 6,
                        }}
                      >
                        {heatmapDays.map((d, i) => (
                          <div
                            key={i}
                            title={`${d.key}: ${d.count} report${d.count !== 1 ? "s" : ""}`}
                            style={{
                              height: 28,
                              borderRadius: 4,
                              background: HEAT_COLORS[Math.min(d.count, 3)],
                              cursor: d.count > 0 ? "pointer" : "default",
                            }}
                          />
                        ))}
                      </div>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(14, 1fr)",
                          gap: 5,
                          marginBottom: 12,
                        }}
                      >
                        {heatmapDays.map((d, i) => (
                          <div
                            key={i}
                            style={{
                              textAlign: "center",
                              fontSize: 9,
                              color: "#999",
                              overflow: "hidden",
                            }}
                          >
                            {d.label.slice(0, 2)}
                          </div>
                        ))}
                      </div>
                      <div
                        className="d-flex align-items-center gap-2"
                        style={{ fontSize: 10, color: "#999" }}
                      >
                        <span>Less</span>
                        {HEAT_COLORS.map((c, i) => (
                          <div
                            key={i}
                            style={{
                              width: 12,
                              height: 12,
                              borderRadius: 2,
                              background: c,
                              border: "1px solid #e0e0e0",
                            }}
                          />
                        ))}
                        <span>More</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Recent reports */}
            <div className="col-md-6">
              <div className="card h-100">
                <div className="card-body">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <h6 className="mb-0" style={{ fontSize: 13 }}>
                      Recent reports
                    </h6>
                    <Link
                      to="progress/workprogress/list"
                      style={{ fontSize: 11, color: "#324336" }}
                    >
                      View all
                    </Link>
                  </div>
                  {wpLoading ? (
                    <CardSpinner height={160} />
                  ) : recentReports.length === 0 ? (
                    <CardEmpty
                      icon="la la-clipboard"
                      message="No reports yet"
                      height={160}
                    />
                  ) : (
                    recentReports.map((r) => (
                      <div
                        key={r._id}
                        className="d-flex align-items-center gap-2 py-2"
                        style={{ borderBottom: "1px solid #f0f0f0" }}
                      >
                        <div
                          style={{
                            width: 32,
                            height: 32,
                            borderRadius: "50%",
                            background:
                              r.reportType === "morning"
                                ? "#fff3cd"
                                : "#cfe2ff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <i
                            className={
                              r.reportType === "morning"
                                ? "la la-sun"
                                : "la la-moon"
                            }
                            style={{
                              fontSize: 14,
                              color:
                                r.reportType === "morning"
                                  ? "#e6a817"
                                  : "#3b7dd8",
                            }}
                          />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              fontSize: 12,
                              fontWeight: 600,
                              color: "#2c2c2c",
                            }}
                          >
                            {formatDate(r.reportDateTime)}
                            <span
                              className="ms-2 badge"
                              style={{
                                fontSize: 10,
                                background:
                                  r.reportType === "morning"
                                    ? "#fff3cd"
                                    : "#cfe2ff",
                                color:
                                  r.reportType === "morning"
                                    ? "#e6a817"
                                    : "#3b7dd8",
                              }}
                            >
                              {r.reportType === "morning" ? "AM" : "PM"}
                            </span>
                          </div>
                          <div style={{ fontSize: 11, color: "#888" }}>
                            {r.reportedByName}
                            {r.contractorCount > 0 &&
                              ` · ${r.contractorCount} contractor${r.contractorCount > 1 ? "s" : ""}`}
                            {r.materialCount > 0 &&
                              ` · ${r.materialCount} material${r.materialCount > 1 ? "s" : ""}`}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── Row 4: Transactions + Quick Links ─────────────────────────────── */}
      {(hasFinance || quickLinks.length > 0) && (
        <div className="row g-3">
          {/* Recent transactions — only if finance access */}
          {hasFinance && (
            <div className={quickLinks.length > 0 ? "col-md-7" : "col-12"}>
              <div className="card h-100">
                <div className="card-body">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <h6 className="mb-0" style={{ fontSize: 13 }}>
                      Recent transactions
                    </h6>
                    <Link
                      to="expenses"
                      style={{ fontSize: 11, color: "#324336" }}
                    >
                      View all
                    </Link>
                  </div>
                  {expLoading ? (
                    <CardSpinner height={180} />
                  ) : recentTxns.length === 0 ? (
                    <CardEmpty
                      icon="la la-exchange-alt"
                      message="No transactions yet"
                      height={180}
                    />
                  ) : (
                    recentTxns.map((txn) => {
                      const isPayment = txn.type === "payment";
                      return (
                        <div
                          key={txn._id}
                          className="d-flex align-items-center gap-3 py-2"
                          style={{ borderBottom: "1px solid #f0f0f0" }}
                        >
                          <div
                            style={{
                              width: 34,
                              height: 34,
                              borderRadius: "50%",
                              background: isPayment ? "#fee2e2" : "#dcfce7",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            <i
                              className={
                                isPayment
                                  ? "la la-arrow-up"
                                  : "la la-arrow-down"
                              }
                              style={{
                                fontSize: 14,
                                color: isPayment ? "#ef4444" : "#22c55e",
                              }}
                            />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div
                              style={{
                                fontSize: 12,
                                fontWeight: 600,
                                color: "#2c2c2c",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {txnLabel(txn)}
                            </div>
                            <div style={{ fontSize: 11, color: "#888" }}>
                              {formatDate(txn.created)}
                            </div>
                          </div>
                          <div
                            style={{
                              fontSize: 13,
                              fontWeight: 600,
                              color: isPayment ? "#ef4444" : "#22c55e",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {isPayment ? "−" : "+"}
                            {formatINR(txn.amount)}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Quick actions — only show if at least one link is accessible */}
          {quickLinks.length > 0 && (
            <div className={hasFinance ? "col-md-5" : "col-12"}>
              <div className="card h-100">
                <div className="card-body">
                  <h6 className="card-title" style={{ fontSize: 13 }}>
                    Quick actions
                  </h6>
                  {quickLinks.map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      style={{ textDecoration: "none" }}
                    >
                      <div
                        className="d-flex align-items-center gap-3 py-2"
                        style={{
                          borderBottom: "1px solid #f0f0f0",
                          cursor: "pointer",
                          transition: "background 0.15s",
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.background = "#fafafa")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.background = "")
                        }
                      >
                        <div
                          style={{
                            width: 34,
                            height: 34,
                            borderRadius: 8,
                            background: item.bg,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <i
                            className={item.icon}
                            style={{ fontSize: 16, color: item.color }}
                          />
                        </div>
                        <span
                          style={{ flex: 1, fontSize: 13, color: "#2c2c2c" }}
                        >
                          {item.label}
                        </span>
                        <i
                          className="la la-angle-right"
                          style={{ fontSize: 12, color: "#bbb" }}
                        />
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default Home;
